import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Info, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { usePartnerAuth } from '@/hooks/usePartnerAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface ProductPricing {
  id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  category: string;
  payout_cents: number;
  retail_price_cents: number;
  is_active: boolean;
}

const PartnerPricing = () => {
  const navigate = useNavigate();
  const { partner, hasPermission, isLoading: partnerAuthLoading } = usePartnerAuth();
  const [products, setProducts] = useState<ProductPricing[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const canViewEarnings = hasPermission('view_earnings');

  useEffect(() => {
    if (!canViewEarnings) return;

    const fetchPricing = async () => {
      if (!partner?.id) return;

      try {
        const { data, error } = await supabase
          .from('partner_product_costs')
          .select(`
            cost_cents,
            retail_price_cents,
            product_id,
            is_active,
            products (
              id,
              name,
              description,
              image_url,
              category
            )
          `)
          .eq('partner_id', partner.id);

        if (error) throw error;

        const formattedProducts: ProductPricing[] = (data || [])
          .filter(item => item.products && item.products.category !== 'repairs')
          .filter(item => {
            if (item.products.category === 'flowers' && !partner?.offers_flowers) return false;
            if (item.products.category === 'cleaning' && !partner?.offers_cleaning) return false;
            return true;
          })
          .map(item => ({
            id: item.products.id,
            name: item.products.name,
            description: item.products.description,
            image_url: item.products.image_url,
            category: item.products.category,
            payout_cents: item.cost_cents,
            retail_price_cents: item.retail_price_cents,
            is_active: item.is_active
          }));

        setProducts(formattedProducts);
      } catch (error) {
        console.error('Error fetching pricing:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPricing();
  }, [partner?.id, partner?.offers_flowers, partner?.offers_cleaning, canViewEarnings]);

  if (partnerAuthLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!canViewEarnings) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  const isOrderSplit = partner?.pontis_order_rate != null;
  const isMonumentCompany = !!partner?.linked_monument_company_id;

  const calculatePayout = (retailCents: number): number => {
    if (!isOrderSplit || !retailCents) return 0;
    // Partner gets their percentage of GROSS (Pontis absorbs Stripe fee)
    return Math.round(retailCents * (1 - partner!.pontis_order_rate!));
  };

  const getCategoryBadgeVariant = (category: string) => {
    switch (category) {
      case 'flowers':
        return 'default';
      case 'cleaning':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const activeProducts = products.filter(p => p.is_active);
  const inactiveProducts = products.filter(p => !p.is_active);

  const groupProducts = (list: ProductPricing[]) => {
    return list.reduce((acc, product) => {
      const category = product.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(product);
      return acc;
    }, {} as Record<string, ProductPricing[]>);
  };

  const categoryOrder = ['flowers', 'cleaning'];
  const sortCategories = (grouped: Record<string, ProductPricing[]>) => {
    return Object.keys(grouped).sort(
      (a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b)
    );
  };

  const categoryLabels: Record<string, string> = {
    flowers: 'Flower Delivery',
    cleaning: 'Headstone Cleaning',
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const getCategoryDescription = (category: string) => {
    return `Your pricing for each ${category} product`;
  };

  const emptyStateMessage = 'Your pricing will appear here once the Pontis team assigns product rates to your account.';

  const renderProductList = (list: ProductPricing[], muted?: boolean) => {
    const grouped = groupProducts(list);
    const sorted = sortCategories(grouped);

    if (list.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <DollarSign className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {muted ? 'No Inactive Products' : 'No Pricing Assigned Yet'}
            </h3>
            <p className="text-muted-foreground text-center max-w-md">
              {muted
                ? 'All your products are currently active.'
                : emptyStateMessage}
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-6">
        {sorted.map((category) => (
          <Card key={category} className={muted ? 'opacity-60' : ''}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                {categoryLabels[category] || category}
                <Badge variant={getCategoryBadgeVariant(category)} className="capitalize">
                  {grouped[category].length} product{grouped[category].length !== 1 ? 's' : ''}
                </Badge>
              </CardTitle>
              <CardDescription>
                {muted
                  ? `Paused ${category} products`
                  : getCategoryDescription(category)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Product</th>
                      {isMonumentCompany && (
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Price</th>
                      )}
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Your Payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[category].map((product) => {
                      const orderSplitProduct = product.payout_cents === 0 && isOrderSplit;
                      const estPayout = orderSplitProduct
                        ? calculatePayout(product.retail_price_cents)
                        : product.payout_cents;
                      const partnerPercent = isOrderSplit
                        ? Math.round((1 - partner!.pontis_order_rate!) * 100)
                        : null;

                      return (
                        <tr key={product.id} className="border-b last:border-0">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              {product.image_url ? (
                                <img
                                  src={product.image_url}
                                  alt={product.name}
                                  className="w-10 h-10 object-cover rounded flex-shrink-0"
                                />
                              ) : (
                                <div className="w-10 h-10 bg-muted rounded flex-shrink-0 flex items-center justify-center">
                                  <span className="text-sm text-muted-foreground">
                                    {product.category === 'flowers' ? '\uD83C\uDF38' : '\uD83E\uDEA3'}
                                  </span>
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{product.name}</p>
                                {product.description && (
                                  <p className="text-xs text-muted-foreground line-clamp-1">{product.description}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          {isMonumentCompany && (
                            <td className="py-3 px-4 text-sm text-right text-muted-foreground">
                              {formatCurrency(product.retail_price_cents)}
                            </td>
                          )}
                          <td className="py-3 px-4 text-sm text-right font-medium text-foreground">
                            {orderSplitProduct ? (
                              <div>
                                <span>{formatCurrency(estPayout)}</span>
                                <span className="block text-xs text-muted-foreground">
                                  ({partnerPercent}% split)
                                </span>
                              </div>
                            ) : (
                              formatCurrency(estPayout)
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">My Pricing</h1>
        <p className="text-muted-foreground">
          Products you fulfill and your payout per item
        </p>
      </div>

      {products.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <DollarSign className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              No Pricing Assigned Yet
            </h3>
            <p className="text-muted-foreground text-center max-w-md">
              {emptyStateMessage}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="active" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="active">
              Active Products
              {activeProducts.length > 0 && (
                <Badge variant="secondary" className="ml-2">{activeProducts.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="inactive">
              Inactive Products
              {inactiveProducts.length > 0 && (
                <Badge variant="secondary" className="ml-2">{inactiveProducts.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            {renderProductList(activeProducts)}
          </TabsContent>

          <TabsContent value="inactive">
            {renderProductList(inactiveProducts, true)}
            {inactiveProducts.length > 0 && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  These products are currently paused. Contact Pontis support if you'd like to reactivate them.
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>
        </Tabs>
      )}

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Need to update your pricing?</strong> Contact Pontis support to request pricing changes.
          Pricing adjustments are handled centrally to ensure consistency across the platform.
        </AlertDescription>
      </Alert>

      {/* Monument Portal cross-link — only for dual-role partners */}
      {partner?.linked_monument_company_id && (
        <Card className="border-l-4 border-l-[#787559] bg-[#FFFEF5]">
          <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6">
            <div>
              <h3 className="text-base font-semibold text-[#212121]">
                Looking for your monument commission rates?
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                View your profit share rates and medallion pricing in the Monument Portal.
              </p>
            </div>
            <Button
              onClick={() => navigate('/portal/rates')}
              className="bg-[#787559] hover:bg-[#787559]/90 text-white shrink-0"
            >
              View Monument Rates
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PartnerPricing;
